import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Headset, PenTool, DollarSign, Settings, Users, Megaphone, Landmark, FileText, Mic, ScanSearch, BarChart3, AlertTriangle, ClipboardList, Sparkles } from 'lucide-react';
type Department = {
  id: string;
  label: string;
  icon: React.ElementType;
  blurb: string;
  useCases: string[];
};
const DEPARTMENTS: Department[] = [{
  id: 'clevel',
  label: 'C-Level',
  icon: Crown,
  blurb: 'Stay ahead of the whole company without living in your inbox.',
  useCases: ['Investor updates', 'Decision log', 'Daily news brief', 'Strategic doc Q&A', 'Voice memo → action items', 'Competitor watch', 'Exec-level reporting & analytics']
}, {
  id: 'support',
  label: 'Customer Support',
  icon: Headset,
  blurb: 'Resolve faster and catch risk before it churns.',
  useCases: ['SLA watchdog', 'Voice-of-customer rollup', 'Churn-risk watcher', 'Onboarding nudges', 'KB article from resolved ticket', 'Ticket triage', 'Dept-lead reporting & analytics']
}, {
  id: 'product',
  label: 'Design / Product',
  icon: PenTool,
  blurb: 'Turn scattered feedback into a clear roadmap signal.',
  useCases: ['Feedback inbox triage', 'Design critique', 'Roadmap insight digest', 'User research synthesizer', 'Bug trend analyzer', 'Release notes generator', 'Spec-to-doc assistant', 'Experiment insights']
}, {
  id: 'sales',
  label: 'Sales',
  icon: DollarSign,
  blurb: 'Keep the pipeline clean and never drop a follow-up.',
  useCases: ['Pipeline digest', 'Deal-risk watcher', 'Call notes → CRM', 'Competitor battlecards', 'Follow-up drafts', 'Forecast rollup']
}, {
  id: 'finance',
  label: 'Finance & Investing',
  icon: Landmark,
  blurb: 'Tighter visibility on spend, runway, and renewals.',
  useCases: ['Spend anomaly watch', 'Board deck prep', 'Invoice triage', 'Cash runway brief', 'Vendor renewal tracker', 'Reporting & analytics']
}, {
  id: 'ops',
  label: 'Operations',
  icon: Settings,
  blurb: 'Codify how work gets done and automate the busywork.',
  useCases: ['SOP Q&A', 'Incident recaps', 'Vendor coordination', 'Process automation', 'Weekly ops brief']
}, {
  id: 'recruiting',
  label: 'Recruitment',
  icon: Users,
  blurb: 'Move candidates through faster with less coordination.',
  useCases: ['Candidate screening notes', 'Interview debrief rollup', 'JD drafting', 'Pipeline status', 'Offer logistics']
}, {
  id: 'marketing',
  label: 'Marketing',
  icon: Megaphone,
  blurb: 'Coordinate launches and roll up performance automatically.',
  useCases: ['Campaign recaps', 'Content briefs', 'Competitor watch', 'Launch coordination', 'Performance rollup']
}];
// Rotate a small set of icons across use-case rows for visual texture.
const ROW_ICONS = [FileText, Mic, ScanSearch, BarChart3, AlertTriangle, ClipboardList, Sparkles];
export function EnterpriseUseCases() {
  const [activeId, setActiveId] = useState(DEPARTMENTS[0].id);
  const active = DEPARTMENTS.find(d => d.id === activeId) ?? DEPARTMENTS[0];
  return <section className="w-full bg-white py-28 md:py-40 border-b border-neutral-200" data-id="element-3158" data-mp="2ljzrzyllfu8a">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-3159" data-mp="2grpxe59ja5zo">
        <div className="text-center mb-16 md:mb-20" data-id="element-3160" data-mp="2gqeqhwkski4c">
          <span className="text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 block" data-id="element-3161" data-mp="2crpg8cv1vgox">
            Built for every team
          </span>
          <h2 className="text-4xl md:text-5xl font-title font-bold text-[#2B211A] mb-6 tracking-tight max-w-3xl md:max-w-4xl mx-auto" data-id="element-3162" data-mp="34htx41jlplle">
            One Giga. Every department.
          </h2>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto" data-id="element-3163" data-mp="30ee3le2tw1cx">
            Giga isn't a single-team tool you roll out and forget. It maps onto
            how your whole company actually works — pick a team to see what it
            does for them.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 md:gap-8 max-w-5xl mx-auto" data-id="element-3164" data-mp="1k3a4q0b4ngic">
          {/* Department selector */}
          <div role="tablist" aria-label="Departments" className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-1 px-1" data-id="element-3165" data-mp="32ztx4enimbi6">
            {DEPARTMENTS.map(dept => {
            const isActive = dept.id === activeId;
            const Icon = dept.icon;
            return <button key={dept.id} role="tab" aria-selected={isActive} onClick={() => setActiveId(dept.id)} className={`relative shrink-0 flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C] ${isActive ? 'bg-[#2B211A] text-white' : 'bg-[#FAF8F4] text-neutral-600 hover:bg-neutral-100 border border-neutral-200'}`} data-id="element-3166" data-mp="2yy00chgfxrna">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isActive ? 'bg-white/10 text-[#F97316]' : 'bg-white text-[#EA580C] border border-neutral-200'}`} data-id="element-3167" data-mp="3hnosk2swqtyy">
                    <Icon size={16} data-id="element-3168" data-mp="269kzqzy23bvh" />
                  </span>
                  <span className="whitespace-nowrap" data-id="element-3169" data-mp="266yho6djhdz0">{dept.label}</span>
                </button>;
          })}
          </div>

          {/* Use-case panel */}
          <div className="bg-[#FAF8F4] rounded-2xl border border-neutral-200 p-6 md:p-8 min-h-[420px]" data-id="element-3170" data-mp="1tpxeteoqpl3n">
            <AnimatePresence mode="wait" data-id="element-3171" data-mp="27p6so7ph434g">
              <motion.div key={active.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.3
            }} data-id="element-3172" data-mp="22rdqdprajt9g">
                <div className="flex items-center gap-3 mb-2" data-id="element-3173" data-mp="1orcgjkt1tbhu">
                  <h3 className="font-title font-bold text-2xl text-[#2B211A]" data-id="element-3174" data-mp="1otp5ihx84m4e">
                    {active.label}
                  </h3>
                </div>
                <p className="text-neutral-600 mb-6" data-id="element-3175" data-mp="wa9sc1nu9rhs">{active.blurb}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-id="element-3176" data-mp="rci0f7n725dl">
                  {active.useCases.map((uc, i) => {
                  const Icon = ROW_ICONS[i % ROW_ICONS.length];
                  return <motion.div key={uc} initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.3,
                    delay: 0.04 * i
                  }} className="flex items-center gap-3 bg-white rounded-xl border border-neutral-200 px-4 py-3" data-id="element-3177" data-mp="2wdazv5nxm1tn" data-mpi={i}>
                        <span className="w-7 h-7 rounded-lg bg-[#FFEDD5] text-[#EA580C] flex items-center justify-center shrink-0" data-id="element-3178" data-mp="2ddx3kh2qcezz">
                          <Icon size={14} data-id="element-3179" data-mp="2ic8eyw8oaxkb" />
                        </span>
                        <span className="text-sm font-medium text-[#2B211A]" data-id="element-3180" data-mp="2mgg1cmrns1fd">
                          {uc}
                        </span>
                      </motion.div>;
                })}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <p className="text-center text-sm text-neutral-400 mt-8" data-id="element-3181" data-mp="1m658whcc9n1i">
          …and many more use cases across the organization.
        </p>
      </div>
    </section>;
}